import round from 'lodash/round'
import { useClipboard } from '@vueuse/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { BehaviorScrollToTop } from '~/utils/interface'
dayjs.extend(customParseFormat)
dayjs.extend(utc)

interface INumberFormatter {
  formatNumber: (value: number, suffix: string) => string
  formatNumberWithDots: (x: number) => string
  formatNumberWithCommas: (x: number | string | null, currencyName?: string) => string
  roundFloatNumber: (number: number, precision: number) => number
  inputOnlyNumber: (value: string) => string
  formatMarkToNumber: (str: string, multiplier?: number) => number
  formatTextAsterisk: (value: string, limit: number) => string
  truncateTo2Decimals: (value: number) => number
  convertToFixedFloat: (num: string | number, fractionDigits: number) => number
  maskedAccountNumber: (num: string | number) => string
}

interface IStringUtils {
  normalizeDiacriticsString: (str?: string) => string
  trimWord: (word?: string) => string
  maskCharacter: (str: string, mask: string, n?: number, isHideBack?: boolean) => string
  capitalizeFirstCharacter: (str: string) => string
  capitalizeFirstAndLastCharacter: (str: string) => string
  capitalizeWords: (str: string) => string
  splitStringAndWrap: (str: string | null | number, wrap?: string) => string
}

interface DeviceHeader {
  os: string
  device: string
  browser: string
}

export const formatAmountUnit = (str: string | number, divider: number = 1000): string => {
  const numberValue = Number(str)
  return Number.isNaN(numberValue) ? '0' : (numberValue / divider).toLocaleString('en-US')
}

export const formatMoney = (value: string | number, multiplier: number = 1, currencyName: string = ''): string => {
  const numberAmount = (typeof value === 'string' ? Number(value.replace(/[^\d.-]/g, '')) : value) * multiplier
  const result = numberAmount.toLocaleString('en-US')
  if (currencyName) {
    return `${result} ${currencyName}`
  }
  return result
}

export type GlobImageI = Record<string, { default: string }>
export const getImageURL = (glob: GlobImageI, filename: string, baseUrl: string, fileType: string = 'png'): string => {
  return glob[`${baseUrl}/${filename?.toLowerCase()}.${fileType}`]?.default
}

export const NumberUtils: INumberFormatter = {
  formatNumber(value: number, suffix: string): string {
    return `${Intl.NumberFormat('en-US').format(value)} ${suffix}`
  },

  formatNumberWithDots(x: number | null): string {
    if (x !== null && x !== undefined) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
    return ''
  },

  roundFloatNumber(number: number, precision: number): number {
    return round(number, precision)
  },

  inputOnlyNumber(value: string): string {
    return value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')
  },

  formatNumberWithCommas(x: number | string | null, currencyName?: string): string {
    let result = ''
    if (x !== null) {
      result = x.toString().replace(/[^\d.-]/g, '')
      const numberList = result.split('.')
      numberList[0] = numberList[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      if (numberList[1]?.length) {
        result = `${numberList[0]}.${numberList[1].substring(0, 2)}`
      } else {
        result = numberList[0]
      }
      if (currencyName) {
        result += ` ${currencyName}`
      }
    }
    return result
  },
  formatMarkToNumber(str: string, multiplier = 1): number {
    if (str !== '') {
      return Number(str.replace(/[^\d.-]/g, '')) * multiplier
    }
    return 0
  },
  formatTextAsterisk(str: string, limit: number): string {
    if (str !== '') {
      const strTemp = str.substring(0, limit)
      if (strTemp.length > 3) {
        return strTemp.replace(strTemp.substring(strTemp.length - 3), '***')
      }
      return strTemp
    }
    return str.toString()
  },
  truncateTo2Decimals(value: number): number {
    return Math.round(value * 100) / 100
  },
  convertToFixedFloat(num: string | number, fractionDigits: number = 4): number {
    if (num) {
      const tempArr = String(num).split(' ')
      const roundedNum = parseFloat(tempArr[0]).toFixed(fractionDigits)
      return parseFloat(roundedNum)
    }
    return 0
  },
  maskedAccountNumber(num: string | number): string {
    if (num) {
      return num
        .toString()
        .replace(/_VP/g, '')
        .replace(/(?<=^.{3}).*(?=.{3}$)/, '****')
    }
    return ''
  }
}

export const StringUtils: IStringUtils = {
  normalizeDiacriticsString(str = ''): string {
    str = str.toLowerCase()
    str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a')
    str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e')
    str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i')
    str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o')
    str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u')
    str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y')
    str = str.replace(/đ/g, 'd')
    // Some system encode vietnamese combining accent as individual utf-8 characters
    str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, '') // Huyền sắc hỏi ngã nặng
    str = str.replace(/\u02C6|\u0306|\u031B/g, '') // Â, Ê, Ă, Ơ, Ư
    return str
  },
  trimWord(word = ''): string {
    return word.trim().replace(/\s\s+/g, ' ')
  },
  maskCharacter(str: string, mask: string, n = 4, isHideBack = false): string {
    if (str?.length <= n) {
      return str
    }
    if (isHideBack) {
      return ('' + str).slice(0, -n) + ('' + str).slice(-n).replace(/./g, mask)
    }
    return ('' + str).slice(0, -n).replace(/./g, mask) + ('' + str).slice(-n)
  },
  capitalizeFirstCharacter(str: string = ''): string {
    return str.charAt(0).toUpperCase() + str.slice(1)
  },
  capitalizeFirstAndLastCharacter(str: string = ''): string {
    return `${str[0].toUpperCase()}${str.slice(1, -1)}${str[str.length - 1].toUpperCase()}`
  },
  capitalizeWords(str: string = ''): string {
    return str.replace(/\b\w/g, function (char: string) {
      return char.toUpperCase()
    })
  },
  splitStringAndWrap(str: string | null | number, wrap: string = 'span'): string {
    const charArray = str?.toString().split('')
    const resultArray = charArray?.map(char => `<${wrap}>${char}</${wrap}>`) ?? []
    return resultArray?.join('')
  }
}

export const getDeviceHeaders = (): DeviceHeader => {
  const deviceHeaders = { os: 'os', device: 'device', browser: 'browser' }
  try {
    const {
      isIos,
      isAndroid,
      isWindows,
      isMacOS,
      isApple,
      isMobile,
      isTablet,
      isDesktop,
      isSafari,
      isFirefox,
      isEdge,
      isChrome,
      isSamsung
    } = useDevice()

    if (isIos) {
      deviceHeaders.os = 'ios'
    } else if (isAndroid) {
      deviceHeaders.os = 'android'
    } else if (isWindows) {
      deviceHeaders.os = 'windows'
    } else if (isMacOS) {
      deviceHeaders.os = 'macOS'
    } else if (isApple) {
      deviceHeaders.os = 'apple'
    }

    if (isMobile || isTablet) {
      deviceHeaders.device = 'mobile'
    } else if (isDesktop) {
      deviceHeaders.device = 'desktop'
    }

    if (isSafari) {
      deviceHeaders.browser = 'safari'
    } else if (isFirefox) {
      deviceHeaders.browser = 'firefox'
    } else if (isEdge) {
      deviceHeaders.browser = 'edge'
    } else if (isChrome) {
      deviceHeaders.browser = 'chrome'
    } else if (isSamsung) {
      deviceHeaders.browser = 'samsung'
    }
  } catch { /* empty */ }

  return deviceHeaders
}

export const getAllUrlParams = (url: string): Record<string, string> => {
  let queryString = url ? url.split('?')[1] : window.location.search.slice(1)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const obj: any = {}

  if (queryString) {
    queryString = queryString.split('#')[0]

    const arr = queryString.split('&')

    for (let i = 0; i < arr.length; i++) {
      const a = arr[i].split('=')

      const paramName = a[0]
      const paramValue = typeof a[1] === 'undefined' ? true : a[1]

      if (paramName.match(/\[(\d+)?\]$/)) {
        const key = paramName.replace(/\[(\d+)?\]/, '')
        if (!obj[key]) {
          obj[key] = []
        }

        if (paramName?.match(/\[\d+\]$/)) {
          const index = /\[(\d+)\]/.exec(paramName) ?? []
          obj[key][index[1]] = paramValue
        } else {
          obj[key].push(paramValue)
        }
      } else if (!obj[paramName]) {
        obj[paramName] = paramValue
      } else if (obj[paramName] && typeof obj[paramName] === 'string') {
        obj[paramName] = [obj[paramName]]
        obj[paramName].push(paramValue)
      } else {
        obj[paramName].push(paramValue)
      }
    }
  }
  return obj
}

export const isValidURL = (text: string): boolean => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ) // fragment locator
  return pattern.test(text)
}

const { copy } = useClipboard()
export const copyString = async (value: string): Promise<void> => {
  await copy(value)
}

export function useScrollIntoView(elementTopRef?: HTMLDivElement | null) {
  if (elementTopRef) {
    elementTopRef.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    })
  }
}

export const scrollToActiveCenterItem = (elementTopRefs: HTMLDivElement[], indexItem: number): void => {
  if (elementTopRefs?.length) {
    elementTopRefs[indexItem]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }
}

export const scrollToTop = (
  top: number = 0,
  behavior: BehaviorScrollToTop = BehaviorScrollToTop.SMOOTH,
  timeout: number = 0
): void => {
  if (window && import.meta.client) {
    setTimeout(() => {
      window.scrollTo({
        top,
        left: 0,
        behavior
      })
    }, timeout)
  }
}

export const formatTimeUtc = (item: string, formatFrom: string, formatTo: string): string => {
  return dayjs.utc(item?.trim(), formatFrom).local().format(formatTo)
}
export const sleep = async (time: number): Promise<object> => {
  return await new Promise(resolve => setTimeout(resolve, time))
}

export const roundDown = (amount: number): number => {
  if (amount) {
    return Math.floor(amount * 100) / 100
  }
  return 0
}

export const getClassNames = (classObj: Record<string, boolean>): string =>
  Object.entries(classObj)
    .filter(([_, value]) => value)
    .map(([key, _]) => key)
    .join(' ')
